import {useEffect} from 'react';

import {recordRumAction} from '../../../util/datadog/datadogRum';
import {type PlotType} from '../../../util/plotHelpers/types';
import {useStablePanelId} from '../../Panel/PanelIDContext';

export type ReactVisUsageContext = {
  areAllLinesuPlotSupported: boolean;
  forceReactVis: boolean;
  isChartTypeuPlotSupported: boolean;
  plotType: PlotType | undefined;
  uplotEnabled: boolean;
};

// Panels that already reported a react-vis render this page load. Keyed by
// stable panel ID (not component instance) so panel-bank virtualization
// remounts don't re-emit. An entry is cleared when the panel switches to
// uPlot, so a later switch back to react-vis is reported again.
const reportedPanelIds = new Set<string>();

/**
 * Reports to Datadog RUM when a line plot panel renders with react-vis
 * instead of uPlot, including which uPlot precondition failed. Emits at most
 * one action per panel per react-vis stretch, so volume is bounded by the
 * number of react-vis panels on the page rather than render count.
 */
export function useReactVisUsageTracking(
  shouldUseuPlot: boolean,
  context: ReactVisUsageContext
): void {
  const stablePanelId = useStablePanelId();
  const {
    areAllLinesuPlotSupported,
    forceReactVis,
    isChartTypeuPlotSupported,
    plotType,
    uplotEnabled,
  } = context;

  useEffect(() => {
    if (shouldUseuPlot) {
      reportedPanelIds.delete(stablePanelId);
      return;
    }
    const alreadyReported = reportedPanelIds.has(stablePanelId);
    if (alreadyReported) {
      return;
    }
    reportedPanelIds.add(stablePanelId);
    recordRumAction('line_plot.usage.react_vis', {
      areAllLinesuPlotSupported,
      forceReactVis,
      isChartTypeuPlotSupported,
      plotType,
      stablePanelId,
      uplotEnabled,
    });
  }, [
    areAllLinesuPlotSupported,
    forceReactVis,
    isChartTypeuPlotSupported,
    plotType,
    shouldUseuPlot,
    stablePanelId,
    uplotEnabled,
  ]);
}
